import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import EmptyMessage from 'components/@common/EmptyResultSetMessage';
import ListViewContainer from 'components/@common/ListViewContainer';
import MoreVertMenu from 'components/@common/MoreVertMenu';
import ProductIconList from 'components/@common/ProductIconList';
import TileViewContainer, { BaseCardData } from 'components/@common/TileViewContainer';
import PageContainer from 'components/@layout/PageContainer';
import GET_CLASSROOM from 'graph/classroom/get-classroom-query';
import useStudentActionOptionsMenu from 'hooks/use-student-action-options-menu';
import { sortBy } from 'lodash';
import Typography from '@material-ui/core/Typography';
import useStore from 'store';
import { ViewStyle } from 'store/types';
import { sortGrade } from 'utilities/grade-util';
import StudentCardContentFragment from './components/StudentCardContentFragment';
import HeaderBar from './HeaderBar';
import { ModuleProps, StudentRowDataProps } from 'types';
import { Student } from 'types/schema-generated-types';
import { filterMatManGroups } from 'utilities/group-utils';

const ClassroomDetails: React.FC<ModuleProps> = props => {
  const [{ ui }] = useStore();
  const { t } = useTranslation('Classroom');
  const match = useRouteMatch<{ classroomId: string }>();
  const classroomId = Number(match.params.classroomId);
  const [modalLoader, setModalLoader] = React.useState(false);
  const { data, loading, error, refetch } = useQuery(GET_CLASSROOM, {
    variables: { id: classroomId }
  });

  const { actionOptions, loading: studentMutationLoading } = useStudentActionOptionsMenu({
    translationId: 'Classroom',
    classroomId,
    classroom: data?.classroom,
    refetchClassroom: refetch
  });

  const getStudents = React.useCallback((): Array<StudentRowDataProps> => {
    if (data?.classroom) {
      const { students, color } = data.classroom;
      const sortedStudents = sortBy(students, ['firstName', 'lastName']);
      return (sortedStudents as Array<Student>).map(
        ({
          id,
          firstName,
          lastName,
          gradeLevel,
          username,
          email,
          password,
          licenseUsage,
          groups
        }) => {
          return {
            firstName,
            lastName,
            id,
            gradeLevel,
            email,
            password,
            username,
            color,
            group:
              filterMatManGroups(groups)?.find(group => group.classroomId === data.classroom.id)
                ?.name ?? 'NA',
            licenseUsage
          };
        }
      );
    }
    return [] as Array<StudentRowDataProps>;
  }, [data]);

  const getCardData = React.useCallback((): Array<BaseCardData> => {
    if (data?.classroom) {
      return getStudents().map(student => {
        const { firstName, lastName, id, color, ...restStudent } = student;

        return {
          title: `${firstName} ${lastName}`,
          classroomId: data.classroom.id,
          organizationId: data.classroom.organizationId,
          id,
          color,
          ContentComponent: <StudentCardContentFragment {...(restStudent as any)} />
        };
      });
    }
    return [] as Array<BaseCardData>;
  }, [getStudents, data]);

  const students = getStudents();
  const getColumns = useCallback(
    () => [
      {
        label: 'First Name',
        sortable: 'true',
        sortKey: 'firstName',
        getValue(item: any): any {
          return <Typography variant="h6">{item.firstName}</Typography>;
        }
      },
      {
        label: 'Last Name',
        sortable: 'true',
        sortKey: 'lastName',
        getValue(item: any): any {
          return <Typography variant="h6">{item.lastName}</Typography>;
        }
      },
      {
        label: 'Group',
        sortable: 'true',
        sortKey: 'group',
        getValue(item: any): any {
          return <Typography>{item.group}</Typography>;
        }
      },
      {
        label: 'Username',
        sortable: 'true',
        sortKey: 'username',
        getValue(item: any): any {
          return <Typography>{item.username}</Typography>;
        }
      },
      {
        label: 'Secret Code',
        sortable: 'true',
        sortKey: 'password',
        getValue(item: any): any {
          return <Typography>{item.password}</Typography>;
        }
      },
      {
        label: 'Email Address',
        sortable: 'true',
        sortKey: 'email',
        getValue(item: any): any {
          return <Typography>{item.email}</Typography>;
        }
      },
      {
        label: 'Grade',
        sortable: 'true',
        sortKey: 'gradeLevel',
        sortFn: (a: any, b: any): number => sortGrade(a.gradeLevel, b.gradeLevel),
        getValue(item: any): any {
          return <Typography>{item.gradeLevel}</Typography>;
        }
      },
      {
        label: 'Products',
        sortable: false,
        sortKey: 'products',
        getValue(item: any): any {
          return <ProductIconList style={{ minWidth: 120 }} licenseUsage={item.licenseUsage} />;
        }
      },
      {
        label: '',
        sortable: false,
        getValue(item: any): any {
          return (
            <MoreVertMenu
              actionOptions={actionOptions}
              id={item.id}
              context={{ classroomId, organizationId: data?.classroom.organizationId }}
            />
          );
        }
      }
    ],
    [actionOptions, classroomId, data]
  );

  return (
    <PageContainer
      loading={loading || modalLoader || studentMutationLoading}
      error={error ? 'Failed to load classes' : null}
    >
      <HeaderBar
        viewControl
        classroom={data?.classroom || {}}
        setLoading={setModalLoader}
        {...props}
        getTitle={(): string => data?.classroom?.name || ''}
      />
      {students?.length &&
        (ui.viewStyle === ViewStyle.LIST && ui.isDesktop ? (
          <ListViewContainer rowData={students} columns={getColumns()} />
        ) : (
          <TileViewContainer actionOptions={actionOptions} cardData={getCardData()} />
        ))}
      {!students?.length && (
        <EmptyMessage
          style={{ marginTop: '12px' }}
          message={t('No Enrolled Students')}
          subtitle={t("There aren't any students enrolled in this class.")}
        />
      )}
    </PageContainer>
  );
};

export default ClassroomDetails;
